import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { SwUpdateService } from '../../services/sw-update.service';

/**
 * Persistent "new version available" prompt for tabs that cannot be reloaded
 * silently.
 *
 * Only non-pristine tabs ever see this: a pristine one self-reloads in
 * SwUpdateService, so by the time the banner renders the user has draft state
 * worth protecting. It is deliberately non-modal and never auto-dismisses --
 * the predecessor was a toast, and it was removed for stacking a fresh copy
 * every 10 minutes. Dismissal is recorded against the version hash, so
 * declining is durable for that deploy and does not suppress the next one.
 *
 * Anchored bottom-centre over the composer. That overlap is intentional per
 * design: it is the one place a user cannot miss, and the cost of missing it
 * is running a weeks-old bundle.
 */
@Component({
    selector: 'app-update-banner',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (swUpdate.updateAvailable()) {
            <div class="update-banner" data-testid="update-banner">
                <svg
                    aria-hidden="true"
                    class="update-banner__icon"
                    fill="none"
                    height="20"
                    viewBox="0 0 20 20"
                    width="20"
                    xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="8.25" stroke="currentColor" stroke-width="1.5" />
                    <path d="M10 9v4.5" stroke="currentColor" stroke-linecap="round" stroke-width="1.5" />
                    <circle cx="10" cy="6.4" fill="currentColor" r="0.9" />
                </svg>
                <span class="update-banner__text" role="status"
                    >A new version of CIQ is available - refresh to get the latest experience.</span
                >
                <button class="update-banner__refresh" data-testid="update-banner-refresh" type="button" (click)="swUpdate.applyUpdate()">
                    Refresh
                </button>
                <button
                    aria-label="Dismiss"
                    class="update-banner__dismiss"
                    data-testid="update-banner-dismiss"
                    type="button"
                    (click)="swUpdate.dismiss()">
                    <svg aria-hidden="true" fill="none" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" stroke-linecap="round" stroke-width="1.75" />
                    </svg>
                </button>
            </div>
        }
    `,
    styles: [
        `
            /* Fixed rather than sticky: the banner has to clear the composer
               regardless of which route is mounted, and no single scroll
               container spans every page it can appear on. */
            .update-banner {
                position: fixed;
                bottom: 1rem;
                left: 50%;
                transform: translateX(-50%);
                z-index: 900;
                display: flex;
                align-items: center;
                gap: 0.75rem;
                width: calc(100vw - 2rem);
                max-width: 56rem;
                padding: 0.875rem 1rem;
                border: 1px solid var(--wa-color-surface-border, #d4d4d8);
                border-radius: var(--wa-border-radius-m, 0.5rem);
                /* Neutral surface, not a semantic colour: per design this must
                   read as chrome rather than a warning or an error. */
                background: var(--wa-color-surface-raised, #ffffff);
                color: var(--wa-color-text-normal, #18181b);
                box-shadow: var(--wa-shadow-l, 0 4px 16px rgb(0 0 0 / 18%));
                font-size: 0.9375rem;
                line-height: 1.375rem;
            }

            .update-banner__icon {
                flex-shrink: 0;
                color: var(--wa-color-text-quiet, #52525b);
            }

            .update-banner__text {
                flex: 1 1 auto;
                min-width: 0;
            }

            .update-banner__refresh {
                flex-shrink: 0;
                padding: 0.4375rem 0.875rem;
                border: none;
                border-radius: var(--wa-border-radius-s, 0.375rem);
                background: var(--wa-color-neutral-fill-loud, #27303f);
                color: var(--wa-color-neutral-on-loud, #ffffff);
                font: inherit;
                font-weight: 600;
                cursor: pointer;
            }

            .update-banner__refresh:hover {
                filter: brightness(1.15);
            }

            .update-banner__dismiss {
                display: flex;
                flex-shrink: 0;
                align-items: center;
                justify-content: center;
                padding: 0.25rem;
                border: none;
                border-radius: var(--wa-border-radius-s, 0.375rem);
                background: none;
                color: var(--wa-color-text-normal, #18181b);
                cursor: pointer;
            }

            .update-banner__dismiss:hover {
                background: var(--wa-color-neutral-fill-quiet, #f4f4f5);
            }

            .update-banner__refresh:focus-visible,
            .update-banner__dismiss:focus-visible {
                outline: 2px solid var(--wa-color-brand-fill-loud, #0284c7);
                outline-offset: 2px;
            }

            /* Narrow viewports: the sentence is the first thing to run out of
               room, so let it wrap and keep both controls reachable. */
            @media (max-width: 30rem) {
                .update-banner {
                    flex-wrap: wrap;
                    gap: 0.5rem 0.75rem;
                }

                .update-banner__text {
                    flex: 1 1 100%;
                    order: 3;
                }

                .update-banner__refresh {
                    margin-left: auto;
                }
            }
        `,
    ],
})
export class UpdateBanner {
    protected readonly swUpdate = inject(SwUpdateService);
}
